import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { getSensor, addSensor, editSensor } from '../../../services/sensors/sensorsService';
import { getSoilSensors } from '../../../services/soilsensors/soilSensorsService';
import { getSimModules } from '../../../services/simModules/simModulesServices';
import { getVariablesConfigurationSensor } from '../../../services/variables/variablesService';

import AutocompleteSimcard from '../../../shared/components/autocompleteSimcard/AutocompleteSimcard';
import VariableCard from '../variableCard/VariableCard'

import { BOOLEAN_OPTIONS } from '../../utils/Constants';
import LoanerToggle from '../../contracts/LoanerToggle';

const booleanOptions = BOOLEAN_OPTIONS;

const dataForVariable = (variables, threshold, isProbe = false, soilNumber = null) => {
    return variables.filter(v => {
        // Si es una probe, filtrar las variables que pertenecen a esa probe
        if (isProbe) {
            return v.probe; // Solo variables de probe
        }
        // Si no es una probe, filtrar las variables que no son de probe
        return !v.probe;
    }).map(v => {
        // Buscar el threshold correspondiente
        const resultado = threshold.find(thr => {
            // Si es una probe, comparar el número de la probe
            if (isProbe) {
                // Extraer el número de la probe del thr.variableId (ej: "TS2" -> 2)
                const thrSoilNumber = parseInt(thr.variableId.replace(/\D/g, "")) || 1;
                // Comparar el thrSoilNumber con el soilNumber de la probe actual
                return thrSoilNumber === soilNumber && thr.variableId.startsWith(v.id);
            }
            // Si no es una probe, comparar directamente los variableId
            return thr.variableId === v.id;
        });

        return {
            variableId: v.id,
            label: v.name,
            unit: v.unit,
            isSelected: !!resultado,
            maxValue: resultado?.maxValue ?? null,
            minValue: resultado?.minValue ?? null,
            maxLimit: resultado?.maxLimit ?? v.maxLimit ?? 1,
            minLimit: resultado?.minLimit ?? v.minLimit ?? 1,
        };

    });
};

const buildThresholdForSend = (data) => {
    return [
        // Variables no asociadas a probes
        ...data.variablesNoProbe.filter(v => v.isSelected).map(v => ({
            variableId: v.variableId,
            maxValue: Number(v.maxValue),
            minValue: Number(v.minValue),
            maxLimit: Number(v.maxLimit),
            minLimit: Number(v.minLimit),
        })),

        // Variables asociadas a probes
        ...data.probes.flatMap((probe, probeIndex) =>
            probe.variables.filter(v => v.isSelected).map((v) => ({
                variableId: (probeIndex + 1 < 2) ? v.variableId : `${v.variableId}${probeIndex + 1}`,
                maxValue: Number(v.maxValue),
                minValue: Number(v.minValue),
                maxLimit: Number(v.maxLimit),
                minLimit: Number(v.minLimit),
            }))
        ),
    ];
};

const buildSensorForSend = (originalObject) => {
    const payload = {
        id: originalObject.id,
        name: originalObject.name,
        active: originalObject.active === "true",
        checked: originalObject.checked,
        startOperation: originalObject.startOperation,
        sendMail: originalObject.sendMail === "true",
        description: originalObject.description,
        sim: originalObject.sim, // Mantener el objeto sim completo
        simModuleId: originalObject.simModuleId,
        gps: originalObject.gps === "true",
        observation: originalObject.observation,
    };
    if (originalObject.hashedApiKey) {
        payload.hashedApiKey = originalObject.hashedApiKey;
    }
    return payload;
};

const getIdsFromVariables = (arr) => {
    return arr
        .filter(item => item.calculated === true) // Filtra solo los que tengan active en true
        .map(item => item.id);                // Devuelve solo los ids
};

const SensorAdView = () => {
    const { sensorId } = useParams();
    const navigate = useNavigate();
    const [soilTypes, setSoilTypes] = useState([]);
    const [simModules, setSimModules] = useState([]);
    const [variables, setVariables] = useState([]);
    const [variablesCalculated, setVariablesCalculated] = useState([]);

    const { register, handleSubmit, reset, control, formState: { errors }, watch } = useForm({
        defaultValues: {
            active: '',
            sendMail: '',
            sim: '',
            simModuleId: '',
            gps: '',
            latitude: "",
            longitude: "",
            hashedApiKey: "",
            // Variables no asociadas a probes
            variablesNoProbe: dataForVariable([], [], false),
            // Probes con sus variables asociadas
            probes: []// probes: [{ soilIdSensor: '', variables: dataForVariable([], false)}],

        }
    });
    // Con setValueAs convertimos el valor a booleano, por lo que gpsValue será true o false
    const gpsValue = watch("gps");

    const variablesNoProbeWatch = watch("variablesNoProbe") || [];
    const showVariableCalculated =
        variablesNoProbeWatch.some(v => v.variableId === "TA" && v.isSelected === true) &&
        variablesNoProbeWatch.some(v => v.variableId === "HA" && v.isSelected === true);

    const { fields: probeFields, append: appendProbe, remove: removeProbe } = useFieldArray({
        control,
        name: 'probes',
    });


    useEffect(() => {
        // cargar variables solo una vez al inicio
        loadVariables();
        loadSoilSensors();
        loadSimModules();
    }, []); // sin variables en dependencias

    // Cuando CREO un sensor nuevo
    useEffect(() => {
        if (sensorId === 'newSensor' && variables.length > 0) {
            reset(prev => ({
                ...prev,
                variablesNoProbe: dataForVariable(variables, [], false),
                probes: [], // o inicializar probes si quieres
            }));
        }
    }, [sensorId, variables, reset]);

    // Cuando se edita el sensor
    useEffect(() => {
        const fetchSensor = async () => {
            if (sensorId !== 'newSensor' && variables.length > 0) { // 👈 aseguramos que ya haya variables cargadas
                try {
                    const response = await getSensor(sensorId);
                    const sortedSoilSensor = response.soilSensor.sort((a, b) => a.soilNumber - b.soilNumber);
                    reset({
                        ...response.sensor,
                        active: response.sensor.active.toString(),
                        sendMail: response.sensor.sendMail.toString(),
                        gps: response.sensor.gps.toString(),
                        startOperation: response.sensor.startOperation.split('T')[0],
                        hashedApiKey: "",
                        variablesNoProbe: dataForVariable(variables, response.threshold, false),
                        probes: sortedSoilSensor.map(soil => ({
                            soilIdSensor: soil.soilIdSensor,
                            variables: dataForVariable(variables, response.threshold, true, soil.soilNumber),
                        })),
                    });
                } catch (error) {
                    console.error(error);
                }
            }
        };

        fetchSensor();
    }, [sensorId, reset, variables]); // aquí sí necesitamos variables, pero no llamamos loadVariables otra vez


    const loadSoilSensors = async () => {
        try {
            const response = await getSoilSensors(1, 10);
            setSoilTypes(response);

        } catch (error) {
            console.error('Error al cargar los sensores del suelo: ', error);
            alert("Error al cargar los sensores del suelo.");
        }
    }

    const loadVariables = async () => {
        try {
            const response = await getVariablesConfigurationSensor();
            setVariables(response);
            setVariablesCalculated(getIdsFromVariables(response));

        } catch (error) {
            console.error('Error al cargar las variables: ', error);
            alert("Error al cargar las variables.");
        }
    }

    const loadSimModules = async () => {
        try {
            const response = await getSimModules(1, 10);
            setSimModules(response);

        } catch (error) {
            console.error('Error al cargar los modulos sim', error);
            alert("Error al cargar los modulos sim.");
        }
    }

    const onSubmit = handleSubmit(async (data) => {
        // event.preventDefault();
        // console.log(data);
        const sensor = buildSensorForSend(data);
        const threshold = buildThresholdForSend(data);
        const soilSensor = data.probes.map((probe, index) => ({
            soilNumber: index + 1, // Asignar un número basado en el índice (empezando desde 1)
            soilIdSensor: probe.soilIdSensor, // Convertir el ID de la probe a cadena si es necesario
        }));


        // console.log("sensor: ", sensor);
        // console.log("threshold: ", threshold);
        // console.log("soilData: ", soilData);
        const body = {
            sensor: sensor,
            threshold: threshold,
            soilSensor: soilSensor
        };

        try {
            if (sensorId !== 'newSensor') {
                const response = await editSensor(sensorId, body);
                if (response.id)
                    alert("Sensor actualizado correctamente");
            }
            else {
                const response = await addSensor(body);
                if (response.id)
                    alert("Sensor creado correctamente");
            }
            navigate('/sensors-ad');

        } catch (error) {
            console.error('Error al enviar el formulario: ', error);
            alert("Error al enviar el formulario. Por favor, intenta de nuevo.");
        }
    });

    const handleCancel = () => {
        navigate('/sensors-ad');
    };


    return (
        <div className='flex flex-col p-3'>
            <div className="flex max-sm:flex-col items-center justify-between mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Sensor</h1>
            </div>

            {sensorId !== 'newSensor' && <LoanerToggle sensorId={sensorId} />}

            <div className="w-full max-w-5xl mx-auto">
                <form onSubmit={onSubmit} className="flex flex-col items-center space-y-4 pb-2">
                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="id" className="block font-semibold text-black mb-1"> Id <span className="text-red-500">*</span></label>
                            <input id="id" type="text" autoComplete="off"
                                name="id"
                                placeholder="Id"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('id', {
                                    required: {
                                        value: true,
                                        message: 'Id es requerido'
                                    },
                                    minLength: {
                                        value: 6,
                                        message: "La longitud mínima debe ser de 6 caracteres."
                                    },
                                    maxLength: {
                                        value: 6,
                                        message: "La longitud máxima debe ser de 6 caracteres."
                                    }
                                })}
                            />
                            {
                                errors.id && <span className="error">{errors.id.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="name" className="block font-semibold text-black mb-1"> Nombre <span className="text-red-500">*</span></label>
                            <input id="name" type="text" autoComplete="off"
                                name="name"
                                placeholder="Nombre"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('name', {
                                    required: {
                                        value: true,
                                        message: 'Nombre es requerido'
                                    },
                                    minLength: {
                                        value: 2,
                                        message: 'Nombre debe tener al menos 2 caracteres'
                                    },
                                    maxLength: {
                                        value: 30,
                                        message: 'Nombre debe tener máximo 30 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.name && <span className="error">{errors.name.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="active" className="block font-semibold text-black mb-1"> Activo <span className="text-red-500">*</span></label>
                            <select id="active"
                                name="active"
                                {...register("active", {
                                    required: {
                                        value: true,
                                        message: 'Activo es requerido'
                                    }
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {booleanOptions.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.active && <span className="error">{errors.active.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="sendMail" className="block font-semibold text-black mb-1"> Correo de alerta <span className="text-red-500">*</span></label>
                            <select id="sendMail"
                                name="sendMail"
                                {...register("sendMail", {
                                    required: {
                                        value: true,
                                        message: 'Correo de alerta es requerido'
                                    }
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled> Seleccione </option>
                                {booleanOptions.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.sendMail && <span className="error">{errors.sendMail.message}</span>
                            }
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="startOperation" className="block font-semibold text-black mb-1"> Inicio de operaciones <span className="text-red-500">*</span></label>
                            <input id="startOperation" type="date" autoComplete="off"
                                name="startOperation"
                                placeholder="Inicio de operaciones"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('startOperation', {
                                    required: {
                                        value: true,
                                        message: 'Inicio de operaciones es requerido'
                                    }
                                })}
                            />
                            {
                                errors.startOperation && <span className="error">{errors.startOperation.message}</span>
                            }
                        </div>

                        <div className='w-full'>
                            <label htmlFor="sim" className="block font-semibold text-black mb-1"> Simcard <span className="text-red-500">*</span></label>
                            <Controller
                                name="sim"
                                control={control}
                                rules={{ required: "Debe seleccionar una simcard" }}
                                render={({ field, fieldState: { error } }) => (
                                    <div>
                                        <AutocompleteSimcard
                                            {...field}
                                            id="sim"
                                            placeholder="Simcard"
                                        />
                                        {error && (
                                            <span className="error">
                                                {error.message}
                                            </span>
                                        )}
                                    </div>
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className="w-full">
                            <label htmlFor="simModuleId" className="block font-semibold text-black mb-1">
                                Modulo Sim <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="simModuleId"
                                {...register("simModuleId", {
                                    required: {
                                        value: true,
                                        message: 'Modulo Sim es requerido'
                                    }
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled>Seleccione</option>
                                {simModules.map((option, index) => (
                                    <option key={index} value={option.id}>{option.name}</option>
                                ))}
                            </select>
                            {
                                errors.simModuleId && <span className="error">{errors.simModuleId.message}</span>
                            }
                        </div>

                        <div className="w-full">
                            <label htmlFor="gps" className="block font-semibold text-black mb-1">
                                Tiene GPS <span className="text-red-500">*</span>
                            </label>
                            <select
                                id="gps"
                                {...register("gps", {
                                    required: {
                                        value: true,
                                        message: 'Operador es requerido'
                                    }
                                })}
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                            >
                                <option value="" disabled>Seleccione</option>
                                {booleanOptions.map((option, index) => (
                                    <option key={index} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            {
                                errors.gps && <span className="error">{errors.gps.message}</span>
                            }
                        </div>
                    </div>

                    {/* Segunda fila: Latitud y Longitud (solo se muestran si "Tiene GPS" es "No") */}
                    {gpsValue === "false" && (
                        <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0 mt-4">
                            <div className="w-full">
                                <label htmlFor="latitude" className="block font-semibold text-black mb-1">
                                    Latitud <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="latitude"
                                    type="number"
                                    autoComplete="off"
                                    placeholder="Latitud"
                                    {...register("latitude", {
                                        required: {
                                            value: true,
                                            message: "La latitud es requerida"
                                        }
                                    })
                                    }
                                    className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                />
                                {errors.latitude && <span className="error">{errors.latitude.message}</span>}
                            </div>
                            <div className="w-full">
                                <label htmlFor="longitude" className="block font-semibold text-black mb-1">
                                    Longitud <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="longitude"
                                    type="number"
                                    autoComplete="off"
                                    placeholder="Longitud"
                                    {...register("longitude", {
                                        required: {
                                            value: true,
                                            message: "La longitud es requerida"
                                        }
                                    })
                                    }
                                    className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md"
                                />
                                {errors.longitude && <span className="error">{errors.longitude.message}</span>}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="hashedApiKey" className="block font-semibold text-black mb-1">
                                Api Key {sensorId === 'newSensor' && <span className="text-red-500">*</span>}
                            </label>
                            <input id="hashedApiKey" type="text" autoComplete="off"
                                name="hashedApiKey"
                                placeholder="Api Key (16 caracteres alfanuméricos)"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                {...register('hashedApiKey', {
                                    required: {
                                        value: sensorId === 'newSensor',
                                        message: 'Api Key es requerida para registrar el sensor'
                                    },
                                    validate: (value) => {
                                        if (!value) return true;
                                        if (!/^[a-zA-Z0-9]{16}$/.test(value)) {
                                            return 'Debe contener exactamente 16 caracteres alfanuméricos';
                                        }
                                        return true;
                                    }
                                })}
                            />
                            {
                                errors.hashedApiKey && <span className="error">{errors.hashedApiKey.message}</span>
                            }
                            <p className="text-xs text-blue-600 mt-1 italic">
                                Nota: Esta clave debe ingresarse solo si se está programando o reconfigurando el sensor físico.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="description" className="block font-semibold text-black mb-1"> Descripción <span className="text-red-500">*</span></label>
                            <textarea id="description" autoComplete="off"
                                name="description"
                                placeholder="Descripción"
                                rows="4"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer resize-none"
                                {...register('description', {
                                    required: {
                                        value: true,
                                        message: 'Descripción es requerido'
                                    },
                                    minLength: {
                                        value: 2,
                                        message: 'Descripción debe tener al menos 2 caracteres'
                                    },
                                    maxLength: {
                                        value: 255,
                                        message: 'Descripción debe tener máximo 255 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.description && <span className="error">{errors.description.message}</span>
                            }

                        </div>
                    </div>

                    <div className="flex flex-col w-full md:flex-row md:space-x-4 space-y-4 md:space-y-0">
                        <div className='w-full'>
                            <label htmlFor="observation" className="block font-semibold text-black mb-1"> Observación </label>
                            <textarea id="observation" autoComplete="off"
                                name="observation"
                                placeholder="Observación"
                                rows="4"
                                className="block w-full p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer resize-none"
                                {...register('observation', {
                                    maxLength: {
                                        value: 255,
                                        message: 'Observación debe tener máximo 255 caracteres'
                                    }
                                })}
                            />
                            {
                                errors.observation && <span className="error">{errors.observation.message}</span>
                            }

                        </div>
                    </div>

                    <div className="flex-col items-center justify-center w-full my-4">
                        <h2 className="text-2xl font-semibold text-blue-600 text-center mb-4">Configuración de umbrales</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"> {/*grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6*/}
                            {variablesNoProbeWatch
                                .filter(variable => {
                                    if (variablesCalculated.includes(variable.variableId) && !showVariableCalculated) {
                                        return false; // no la muestro
                                    }
                                    return true;
                                })
                                .map((variable, index) => (
                                    <div key={variable.variableId || `fallback-key-${index}`} className="w-full">
                                        <VariableCard
                                            key={`${variable.variableId}.${index}`}
                                            variable={variable}
                                            register={register}
                                            index={index}
                                            prefix="variablesNoProbe"
                                            watch={watch}
                                            unit={variable.unit}
                                            errors={errors}
                                        />
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="flex-col items-center justify-center w-full my-4">
                        {/* Variables asociadas a probes */}
                        <h2 className="text-2xl font-semibold text-blue-600 text-center mb-4">Configuración de sondas</h2>

                        {probeFields.map((probe, probeIndex) => (
                            <div key={probe.id} className="border rounded-lg p-4 bg-white shadow-sm mb-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-blue-600 italic">
                                        Probe {probeIndex + 1}
                                    </h3>

                                    <button type="button"
                                        onClick={() => removeProbe(probeIndex)}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-gray-100 rounded-lg border border-red-200 hover:border-red-500 hover:bg-red-500 transition-colors duration-200"
                                    >
                                        <span className="material-icons-outlined mr-2">delete</span> Eliminar sonda
                                    </button>
                                </div>

                                <div className='w-full mb-4'>
                                    {/* Selector de Tipo de sonda */}
                                    <label htmlFor={`soilIdSensor-${probeIndex}`} className="block font-semibold text-black mb-1">
                                        Tipo de sonda <span className="text-red-500">*</span>
                                    </label>
                                    <Controller
                                        name={`probes.${probeIndex}.soilIdSensor`}
                                        control={control}
                                        rules={{ required: 'Tipo de sonda es requerido' }}
                                        render={({ field }) => (
                                            <select
                                                {...field}
                                                id={`soilIdSensor-${probeIndex}`}
                                                className="block w-full md:w-1/2 p-2 bg-white border border-gray-200 text-black text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 rounded shadow-md cursor-pointer"
                                            >
                                                <option value="">Seleccione</option>
                                                {soilTypes.length > 0 &&
                                                    soilTypes.map((option, index) => (
                                                        <option key={index} value={option.id}>
                                                            {option.id}
                                                        </option>
                                                    ))}
                                            </select>
                                        )}
                                    />
                                </div>

                                {/* Variables de la probe */}
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {watch(`probes.${probeIndex}.variables`).map((variable, index) => (
                                        <div key={variable.variableId || `fallback-key-${index}`} className="w-full">
                                            <VariableCard
                                                key={`${variable.variableId}.${index}`}
                                                variable={variable}
                                                register={register}
                                                index={index}
                                                prefix={`probes.${probeIndex}.variables`}
                                                watch={watch}
                                                unit={variable.unit}
                                                errors={errors}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Botón para agregar una nueva sonda */}
                        <div className="w-full max-w-xl mx-auto">
                            <button
                                type="button"
                                onClick={() => appendProbe({ soilIdSensor: '', variables: dataForVariable(variables, [], true) })}
                                className="flex flex-1 rounded w-full justify-center text-white bg-blue-600 hover:bg-blue-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200"
                            >
                                Agregar sonda
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-center md:space-x-4">
                        <div className="w-full">
                            <button type="submit" className="flex flex-1 rounded w-full justify-center text-white bg-blue-600 hover:bg-blue-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Guardar
                            </button>
                        </div>

                        <div className="w-full">
                            <button type="button" onClick={handleCancel} className="flex flex-1 rounded w-full justify-center text-white bg-red-600 hover:bg-red-800 font-medium px-5 py-2.5 me-2 mb-2 transition duration-200">
                                Cancelar
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SensorAdView;